import React, { useEffect, useState } from "react";
import Footer from "../../../../generic/Footer";
import Header from "../../../../generic/Header";
import Helmet from "../../../../functional-component/Helmet";
import { useCookies } from "react-cookie";
import { config } from "../../../../../config/config";
import { generateFaqSchema, generateSchema } from "../../../../../common/common";

import PainterHeroBanner from "./PainterHeroBanner";
import OurPaintingServices from "./OurPaintingServices";
import PainterGallery from "./PainterGallery";
import PainterWhyChooseUs from "./PainterWhyChooseUs";
import PainterProcess from "./PainterProcess";
import PainterCtaBanner from "./PainterCtaBanner";
import PainterTestimonials from "./PainterTestimonials";
import PainterEnquiry from "./PainterEnquiry";
import PainterFaq from "./PainterFaq";
import PainterAutoPopup from "./PainterAutoPopup";
import "./PainterServiceSections.css";

import PainterHeroImage from "../../assets/painting-service.jpg";
import InteriorPaintingImage from "../../assets/interior-painting-service.jpg";
import ExteriorPaintingImage from "../../assets/exterior-painting-service.jpg";
import CommercialPaintingImage from "../../assets/commercial-painting-service.jpg";
import SpecialityPaintingImage from "../../assets/speciality-painting-service.jpg";

import shieldIcon from "../../assets/icons/high-quality.png";
import brushIcon from "../../assets/icons/paint-brush.png";
import budgetIcon from "../../assets/icons/budget.png";
import clockIcon from "../../assets/icons/checklist.png";
import consultationIcon from "../../assets/icons/conversation.png";
import preparationIcon from "../../assets/icons/preparation.png";
import paintingIcon from "../../assets/icons/paint-roller.png";
import cleanupIcon from "../../assets/icons/data-cleanup.png";

const PainterService = () => {
  const [cookies] = useCookies();
  const city = cookies[config.preferencesCookie]?.city || "bangalore";
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  const landingPageContent = {
    leadSource: "Painter Service",
    heroSection: {
      heroTitle: "Skilled Painters for a Flawless Finish, Every Time",
      heroTitleDes:
        "Hire experienced, background-verified painters for interior, exterior, and commercial projects. Premium paints, precise workmanship, and a spotless finish, guaranteed.",
      heroList: [
        "Verified & Experienced Painters",
        "Free Colour Consultation",
        "Premium & Eco-Friendly Paints",
        "On-Time, Mess-Free Work",
      ],
      heroButton: "Get a Free Quote",
      formTitle: "Get a Free Painting Quote",
      bannerImage: PainterHeroImage,
    },
    faq: {
      title: "Frequently Asked Questions",
      titleDesc: "Common questions homeowners ask before booking a painter with us.",
      main: [
        {
          question: "How do I book a painter through Get My Housing?",
          answer:
            "Simply fill out the enquiry form or call us with your requirements. Our team will schedule a free site visit and share a detailed quote before work begins.",
        },
        {
          question: "Are your painters trained and background-verified?",
          answer:
            "Yes, every painter on our platform is experienced, trained in modern techniques, and verified for your safety and peace of mind.",
        },
        {
          question: "Can you help me choose the right paint colours?",
          answer:
            "Absolutely. Our team offers free colour consultation to help you pick shades and finishes that suit your space and lighting.",
        },
        {
          question: "What paint brands do you work with?",
          answer:
            "We work with leading paint brands and offer both premium and budget-friendly options, including washable and eco-friendly variants.",
        },
        {
          question: "How long does a typical painting job take?",
          answer:
            "A standard 2-3 BHK home usually takes 3-5 days depending on the scope of work, while single rooms can be completed in a day.",
        },
        {
          question: "Do you provide a warranty on painting work?",
          answer:
            "Yes, we offer a workmanship warranty so you can be confident in the durability and finish of the painting job.",
        },
      ],
    },
    enquirySection: {
      title: "Ready to Repaint Your Space?",
      titleDesc:
        "Share your details and our painting expert will call you back with a free, no-obligation quote.",
    },
    testimonialSection: {
      title: "What Our Customers Are Saying",
      titleDesc: "Real experiences from homeowners who trusted our painters with their spaces.",
      main: [
        {
          name: "Arvind Rao",
          feedback:
            "The painter team was punctual, neat, and extremely skilled. My living room looks stunning with the new colour scheme they suggested.",
          location: "Bangalore, India",
        },
        {
          name: "Meera Nair",
          feedback:
            "Loved the free colour consultation. The finish is smooth and even, and they cleaned up completely after finishing the job.",
          location: "Chennai, India",
        },
        {
          name: "Sanjay Verma",
          feedback:
            "Got our office exterior repainted. Professional crew, good quality paint, and they finished a day ahead of schedule.",
          location: "Hyderabad, India",
        },
        {
          name: "Divya Krishnan",
          feedback:
            "Very transparent pricing with no hidden charges. The texture wall they did for my bedroom turned out beautifully.",
          location: "Pune, India",
        },
      ],
    },
    whyChooseUs: {
      title: "Why Homeowners Choose Our Painters",
      titleDesc: "Reliable, skilled, and detail-oriented painters who treat your home like their own.",
      main: [
        {
          icon: shieldIcon,
          title: "Verified & Trained Painters",
          description: "Every painter is background-checked and trained in the latest techniques and safety practices.",
        },
        {
          icon: brushIcon,
          title: "Premium Paints & Finishes",
          description: "We use top-quality, long-lasting paints for a smooth, durable, and vibrant finish.",
        },
        {
          icon: clockIcon,
          title: "On-Time, Mess-Free Work",
          description: "Projects are completed on schedule with complete protection for your furniture and floors.",
        },
        {
          icon: budgetIcon,
          title: "Transparent, Fair Pricing",
          description: "Clear, upfront quotes with no hidden costs, so you know exactly what you are paying for.",
        },
      ],
    },
    ourProcess: {
      title: "How It Works",
      titleDesc: "A simple, streamlined process from enquiry to a beautifully painted space.",
      main: [
        {
          icon: consultationIcon,
          title: "Free Consultation",
          description: "Share your requirements and get expert colour and finish recommendations at no cost.",
        },
        {
          icon: preparationIcon,
          title: "Surface Preparation",
          description: "We clean, fill cracks, sand, and prime surfaces for a flawless base coat.",
        },
        {
          icon: paintingIcon,
          title: "Expert Painting",
          description: "Skilled painters apply premium paint with precision, care, and attention to detail.",
        },
        {
          icon: cleanupIcon,
          title: "Final Inspection",
          description: "We conduct a thorough quality check and clean-up before handing over your space.",
        },
      ],
    },
    gallery: [
      { image: InteriorPaintingImage, title: "Interior Painting" },
      { image: ExteriorPaintingImage, title: "Exterior Painting" },
      { image: CommercialPaintingImage, title: "Commercial Painting" },
      { image: SpecialityPaintingImage, title: "Texture & Speciality" },
    ],
  };

  const faqSchema = generateFaqSchema(landingPageContent.faq);
  const schema = generateSchema("Painter Service", city);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".pnt-fade").forEach((element) => observer.observe(element));

    const timer = window.setTimeout(() => {
      setIsLeadModalOpen(true);
    }, 700);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <Helmet
        title={`Professional Painter Service in ${city} | Get My Housing`}
        description={`Hire verified, skilled painters in ${city} for interior, exterior, and commercial painting. Free colour consultation, premium paints, and on-time delivery.`}
        keywords="painter service, house painters, home painting, wall painting contractor, professional painters, Get My Housing"
        canonicalUrl={window.location.href}
        ogTitle={`Professional Painter Service in ${city} | Get My Housing`}
        ogDescription={`Hire verified, skilled painters in ${city} for interior, exterior, and commercial painting. Free colour consultation, premium paints, and on-time delivery.`}
        twitterTitle={`Professional Painter Service in ${city} | Get My Housing`}
        twitterDescription={`Hire verified, skilled painters in ${city} for interior, exterior, and commercial painting. Free colour consultation, premium paints, and on-time delivery.`}
        ogImage={PainterHeroImage}
        schema={[schema, faqSchema]}
      />
      <PainterAutoPopup
        isOpen={isLeadModalOpen}
        onClose={() => setIsLeadModalOpen(false)}
        productUrl={typeof window !== "undefined" ? window.location.href : ""}
      />
      <Header />

      <PainterHeroBanner content={landingPageContent} />

      <section className="pnt-services-section">
        <div className="pnt-container">
          <div className="pnt-section-head">
            <span className="pnt-tag">What We Offer</span>
            <h2 className="pnt-heading">Our Painting Services</h2>
            <p className="pnt-subheading">
              From a single accent wall to a full building repaint, our painters handle it all.
            </p>
          </div>
        </div>
        <OurPaintingServices />
      </section>

      <PainterGallery gallery={landingPageContent.gallery} />

      <PainterWhyChooseUs data={landingPageContent.whyChooseUs} />

      <PainterCtaBanner />

      <PainterProcess data={landingPageContent.ourProcess} />

      <PainterTestimonials data={landingPageContent.testimonialSection} />

      <PainterEnquiry content={landingPageContent} />

      <PainterFaq data={landingPageContent.faq} />

      <Footer />
    </>
  );
};

export default PainterService;
